import { useEffect, useMemo, useState } from "react";
import NavBar from "../../component/navBar/NavBar";
import Title from "../../component/title/Title";
import "./Notifications.css";
import { CampaignForm } from "./CampaignForm";
import Table from "../../component/table/Table";
import { useAuth } from "../../config/utilities/useAuth";
import SnackBar from "../../component/Snackbar/Snackbar";
import DeleteModal from "../../component/modal/DeleteModal";
import {
  APICreateCampaign,
  APIDeleteCampaign,
  APIGetCampaigns,
  APIRunCampaign,
  APIUpdateCampaign,
} from "../../config/utilities/APIs";
import {
  formatToISODateTime,
  getUTCOffset,
  parseISODateTime,
} from "../../config/utilities/fDate";
import { useTimezone } from "../../config/utilities/useTimezone";
import { getDefaultAudienceFields } from "./campaignFormHelpers.js";
import { ENABLE_EXTENDED_CAMPAIGN_AUDIENCES } from "../../config/featureFlags";
import {
  formatCampaignAudienceForApi,
  formatCampaignAudienceSummary,
  parseCampaignAudienceFromApi,
} from "../../config/utilities/campaignAudiencePayload.js";

const CAMPAIGN_STATUSES = {
  SCHEDULED: "Scheduled",
  ON_DEMAND: "On Demand",
  PUBLISHED: "Published",
};

const statusOptions = [
  { label: "Scheduled", value: CAMPAIGN_STATUSES.SCHEDULED },
  { label: "On Demand", value: CAMPAIGN_STATUSES.ON_DEMAND },
  { label: "Published", value: CAMPAIGN_STATUSES.PUBLISHED },
];

export default function Notifications() {
  const { privilege, token } = useAuth();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [action, setAction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [fetchedCampaignData, setFetchedCampaignData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const { timezone } = useTimezone();
  const [selectedFilters, setSelectedFilters] = useState({
    status: "",
  });

  const filters = [
    { name: "status", placeholder: "Filter by Status", options: statusOptions },
  ];

  const [input, setInput] = useState({
    name: "",
    description: "",
    alertType: "",
    scheduledDate: "",
    scheduledTime: "",
    message: "",
    targetAudience: "",
    ...getDefaultAudienceFields(),
  });

  const [error, setError] = useState({
    name: false,
    description: false,
    scheduledDateTime: false,
    message: false,
    targetAudience: false,
    scheduleId: false,
    scheduleIds: false,
    scheduleAudience: "",
  });

  const headers = useMemo(() => {
    const offset = getUTCOffset(timezone);

    return [
      { key: "name", name: "Campaign", sortable: false },
      { key: "targetAudience", name: "Target Audience", sortable: false },
      {
        key: "lastTriggered",
        name: `Last Triggered (${offset})`,
        sortable: false,
      },
      {
        key: "scheduledTime",
        name: `Scheduled Time (${offset})`,
        sortable: false,
      },
      { key: "status", name: "Status", sortable: false },
    ];
  }, [timezone]);

  const fetchCampaigns = async (page = 1, filters = selectedFilters) => {
    setLoading(true);

    try {
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== ""),
      );
      const response = await APIGetCampaigns(
        page,
        filterParams,
        setLoading,
        institutionId,
      );

      if (response.status === 200) {
        const { data } = response.data;
        const normalizedCampaigns = (data?.campaigns || []).map((campaign) => ({
          ...campaign,
          targetAudienceSummary: formatCampaignAudienceSummary(campaign) || "—",
          status:
            campaign?.lastTriggered &&
            campaign?.status === CAMPAIGN_STATUSES.SCHEDULED
              ? CAMPAIGN_STATUSES.PUBLISHED
              : campaign?.status,
        }));

        setFetchedCampaignData({
          ...data,
          campaigns: normalizedCampaigns,
        });
        setPageNumber(page);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    fetchCampaigns(1, selectedFilters);
  }, [selectedFilters]);

  const handleReset = async () => {
    setAction(null);
    setShowDeleteModal(false);
    setLoading(false);
    setSelectedRowData(null);
    setFetchedCampaignData([]);
    setInput({
      name: "",
      description: "",
      alertType: "",
      scheduledDate: "",
      scheduledTime: "",
      message: "",
      targetAudience: "",
      ...getDefaultAudienceFields(),
    });
    setError({
      name: false,
      description: false,
      alertType: false,
      scheduledDate: false,
      scheduledTime: false,
      message: false,
      targetAudience: false,
      scheduleId: false,
      scheduleIds: false,
      scheduleAudience: "",
    });
    setPageNumber(1);
    await fetchCampaigns(1, selectedFilters);
  };

  const handleEdit = async () => {
    setLoading(true);

    const formattedInput = formatInput(input);
    setLoading(false);

    try {
      const response = await APIUpdateCampaign(
        formattedInput,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Update Successful");
        setSnackbarSubtitle("Campaign has been updated successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to update campaign",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Update Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleCreate = async () => {
    setLoading(true);

    const formattedInput = formatInput(input);

    try {
      const response = await APICreateCampaign(
        formattedInput,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Create Successful");
        setSnackbarSubtitle("Campaign has been created successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to create campaign",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Create Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleDelete = async (row) => {
    setLoading(true);

    const rowId = row._id;

    try {
      const response = await APIDeleteCampaign(
        rowId,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Delete Successful");
        setSnackbarSubtitle("Campaign has been deleted successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to delete campaign",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Delete Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleOpenCreate = () => {
    setAction("create");
  };

  const handleRunCampaign = async (campaignId) => {
    setLoading(true);

    try {
      const response = await APIRunCampaign(
        campaignId,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Campaign Run Successful");
        setSnackbarSubtitle("Campaign has been triggered successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to run campaign",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Campaign Run Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handlePopoverAction = (action, rowId) => {
    const selectedRow = fetchedCampaignData.campaigns.find(
      (row) => row._id === rowId,
    );
    if (action === "edit") {
      const formattedInput = parseFormattedInput(selectedRow);
      setInput(formattedInput);
      setSelectedRowData(formattedInput);
      setAction("edit");
    } else if (action === "delete") {
      setSelectedRowData(selectedRow);
      setShowDeleteModal(true);
    } else if (action === "runCampaign") {
      handleRunCampaign(selectedRow._id);
    } else {
      const formattedInput = parseFormattedInput(selectedRow);
      setInput(formattedInput);
      setSelectedRowData(formattedInput);
      setAction("view");
    }
  };

  const formatInput = (input) => {
    let alertTime = {};

    if (input.alertType === "sendNow") {
      alertTime = { sendNow: { isEnabled: true } };
    } else if (input.alertType === "onDemand") {
      alertTime = { onDemand: { isEnabled: true } };
    } else if (
      input.alertType === "scheduled" &&
      input.scheduledDate &&
      input.scheduledTime
    ) {
      const formattedDateTime = formatToISODateTime(
        input.scheduledDate,
        input.scheduledTime,
        timezone,
      );
      if (formattedDateTime) {
        alertTime = {
          scheduled: {
            isEnabled: true,
            datetime: formattedDateTime,
          },
        };
      }
    }
    const {
      scheduleId,
      scheduleIds,
      tripTypeIds,
      tripTypeScope,
      tripTypeDate,
      familyIds,
      stopIds,
      campus,
      targetAudience,
      ...rest
    } = input;

    const audiencePayload = formatCampaignAudienceForApi(
      {
        targetAudience,
        scheduleId,
        scheduleIds,
        tripTypeIds,
        tripTypeScope,
        tripTypeDate,
        familyIds,
        stopIds,
        campus,
      },
      { extendedEnabled: ENABLE_EXTENDED_CAMPAIGN_AUDIENCES },
    );

    const formattedInput = {
      ...rest,
      ...audiencePayload,
      alertTime,
      ...(input._id ? { _id: input._id } : {}),
    };

    return formattedInput;
  };

  const parseFormattedInput = (formattedInput) => {
    const {
      name = "",
      description = "",
      message = "",
      alertTime = {},
      _id = "",
    } = formattedInput;
    let alertType = "";
    let scheduledDate = "";
    let scheduledTime = "";

    if (alertTime.sendNow?.isEnabled) {
      alertType = "sendNow";
    } else if (alertTime.onDemand?.isEnabled) {
      alertType = "onDemand";
    } else if (alertTime.scheduled?.isEnabled && alertTime.scheduled.datetime) {
      const [date, time] = parseISODateTime(
        alertTime.scheduled.datetime,
        timezone,
      );
      scheduledDate = date || "";
      scheduledTime = time || "";
      alertType = "scheduled";
    }

    const audienceFields = parseCampaignAudienceFromApi(formattedInput);

    return {
      _id,
      name,
      description,
      message,
      alertType,
      scheduledDate,
      scheduledTime,
      ...audienceFields,
      familyDetails: formattedInput.familyDetails,
      scheduleDetails: formattedInput.scheduleDetails,
      stopDetails: formattedInput.stopDetails,
      campusDetails: formattedInput.campusDetails,
    };
  };

  const renderTitle = () => {
    switch (action) {
      case "edit":
        return (
          <Title
            title="Edit Campaign"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "create":
        return (
          <Title
            title="Create Campaign"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "view":
        <Title
          title="View Campaign"
          titleSize="1.5rem"
          gap="0.5rem"
          lineHeight="2.5rem"
        />;
      default:
        return (
          <Title
            title="Notifications Campaign"
            subtitle="Manage the campaigns which are sent out to parents"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );
    }
  };

  const renderContent = () => {
    switch (action) {
      case "edit":
        return (
          <CampaignForm
            mode="edit"
            existingData={selectedRowData}
            onSubmit={handleEdit}
            handleReset={handleReset}
            loading={loading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      case "create":
        return (
          <CampaignForm
            mode="create"
            onSubmit={handleCreate}
            handleReset={handleReset}
            loading={loading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      case "view":
        return (
          <CampaignForm
            mode="view"
            existingData={selectedRowData}
            handleReset={handleReset}
            loading={loading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
          />
        );
      default:
        return (
          <Table
            data={fetchedCampaignData?.campaigns || []}
            searchText={"Search by campaign name..."}
            headers={headers}
            showEmptyHeader={true}
            showFilter={true}
            showSearchBar={false}
            handlePopoverAction={handlePopoverAction}
            onClick={handleOpenCreate}
            module="notifications"
            loading={loading}
            showTableAction={
              role === "ADMIN" || role === "SUPERADMIN" || role === "EDITOR"
            }
            useBackendPagination={true}
            onPageChange={fetchCampaigns}
            totalPages={fetchedCampaignData?.pagination?.totalPages || 1}
            currentPage={pageNumber}
            filters={filters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            height="75vh"
            totalRecords={fetchedCampaignData?.pagination?.total || 0}
          />
        );
    }
  };

  return (
    <div
      className={`main-page notifications-page ${
        !action ? "notifications-table-view" : ""
      }`}
    >
      <NavBar type="notifications" action={action} handleReset={handleReset} />
      <div className="content-area notifications-content-area">
        <div className="title-container mobile-hide-page-title">{renderTitle()}</div>
        <div className="main-content-container notifications-main-content-container">
          {renderContent()}
        </div>
      </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        itemName={selectedRowData}
        module="notifications"
        loading={loading}
      />
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </div>
  );
}
